import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { MapPin, UserRound } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import DestinatarioSelector from '@/components/ordini/DestinatarioSelector'
import { useDestinatario } from '@/hooks/useOrdini'
import { creaDestinatarioDaCliente } from '@/api/ordini'
import { toast } from 'sonner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const RITIRO_IN_SEDE = 'ritiro_in_sede'
const INDIRIZZO_RITIRO = {
  indirizzo_via: 'Via Comunale Margherita',
  indirizzo_civico: '245',
  indirizzo_cap: '80145',
  indirizzo_citta: 'Napoli',
  indirizzo_provincia: 'NA',
  indirizzo_nazione: 'IT',
  indirizzo_presso: 'Tavernetta Colauri',
  indirizzo_note: '',
}
const CAMPI_INDIRIZZO = Object.keys(INDIRIZZO_RITIRO)

/**
 * Step 1: Dati Spedizione
 * 
 * Form per corriere, tracking, indirizzo completo
 * 
 * Features:
 * - Pulsante "Usa indirizzo cliente" per auto-compilare da indirizzo predefinito
 */
export default function SpedizioneWizardStep1({ data, ordine, onComplete, onBack }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = useForm({
    defaultValues: data,
    mode: 'onChange',
  })
  const indirizzoPreRitiro = useRef(null)
  const [isCreatingFromCliente, setIsCreatingFromCliente] = useState(false)
  const isRitiroInSede = watch('corriere') === RITIRO_IN_SEDE
  const destinatarioId = watch('destinatario_id')
  const indirizzoDestinatarioId = watch('indirizzo_destinatario_id')
  const { data: destinatarioSelezionato } = useDestinatario(destinatarioId)
  const indirizziDestinatario = destinatarioSelezionato?.indirizzi || []

  const compilaIndirizzo = (indirizzo) => {
    CAMPI_INDIRIZZO.forEach((campo) => {
      setValue(campo, indirizzo[campo] || '', { shouldValidate: true })
    })
  }

  const onSubmit = (formData) => {
    onComplete(formData)
  }

  const compilaDaIndirizzoDestinatario = (indirizzo, destinatario = destinatarioSelezionato) => {
    if (!indirizzo || isRitiroInSede) return
    compilaIndirizzo({
      indirizzo_via: indirizzo.via,
      indirizzo_civico: indirizzo.civico,
      indirizzo_cap: indirizzo.cap,
      indirizzo_citta: indirizzo.citta,
      indirizzo_provincia: indirizzo.provincia,
      indirizzo_nazione: indirizzo.nazione || 'IT',
      indirizzo_presso: destinatario?.nome_completo || '',
      indirizzo_note: indirizzo.note || '',
    })
  }

  const handleDestinatarioChange = (id, destinatario) => {
    setValue('destinatario_id', id, { shouldValidate: true })
    const indirizzi = destinatario?.indirizzi || []
    const indirizzo = indirizzi.find((item) => item.predefinito) || indirizzi[0]
    setValue('indirizzo_destinatario_id', indirizzo?.id || null)
    if (indirizzo) compilaDaIndirizzoDestinatario(indirizzo, destinatario)
  }

  const handleIndirizzoDestinatarioChange = (id) => {
    const indirizzo = indirizziDestinatario.find((item) => String(item.id) === String(id))
    setValue('indirizzo_destinatario_id', indirizzo?.id || null)
    compilaDaIndirizzoDestinatario(indirizzo)
  }

  const handleUsaClienteComeDestinatario = async () => {
    if (!ordine?.cliente?.id) return
    setIsCreatingFromCliente(true)
    try {
      const response = await creaDestinatarioDaCliente(ordine.cliente.id)
      const { destinatario, message } = response.data
      handleDestinatarioChange(destinatario.id, destinatario)
      toast.success(message)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Errore nella selezione del destinatario')
    } finally {
      setIsCreatingFromCliente(false)
    }
  }

  // Trova indirizzo predefinito del cliente
  const indirizzoCliente = ordine?.cliente?.indirizzi?.find(i => i.default) 
    || ordine?.cliente?.indirizzi?.[0]

  // Compila indirizzo da cliente
  const handleUsaIndirizzoCliente = () => {
    if (indirizzoCliente) {
      compilaIndirizzo({
        indirizzo_via: indirizzoCliente.via,
        indirizzo_civico: indirizzoCliente.civico,
        indirizzo_cap: indirizzoCliente.cap,
        indirizzo_citta: indirizzoCliente.citta,
        indirizzo_provincia: indirizzoCliente.provincia,
        indirizzo_nazione: indirizzoCliente.nazione || 'IT',
        indirizzo_presso: indirizzoCliente.destinatario,
        indirizzo_note: indirizzoCliente.note,
      })
    }
  }

  const handleCorriereChange = (value) => {
    if (value === RITIRO_IN_SEDE && !isRitiroInSede) {
      indirizzoPreRitiro.current = {
        ...Object.fromEntries(CAMPI_INDIRIZZO.map((campo) => [campo, getValues(campo)])),
        tracking_number: getValues('tracking_number'),
        salva_indirizzo_cliente: getValues('salva_indirizzo_cliente'),
      }
      compilaIndirizzo(INDIRIZZO_RITIRO)
      setValue('tracking_number', '')
      setValue('salva_indirizzo_cliente', false)
    } else if (isRitiroInSede && value !== RITIRO_IN_SEDE) {
      if (indirizzoPreRitiro.current) {
        compilaIndirizzo(indirizzoPreRitiro.current)
        setValue('tracking_number', indirizzoPreRitiro.current.tracking_number || '')
        setValue('salva_indirizzo_cliente', indirizzoPreRitiro.current.salva_indirizzo_cliente || false)
      } else if (indirizzoCliente) {
        handleUsaIndirizzoCliente()
      } else {
        compilaIndirizzo({ indirizzo_nazione: 'IT' })
      }
      indirizzoPreRitiro.current = null
    }
    setValue('corriere', value, { shouldValidate: true })
  }

  // Auto-compila al mount se indirizzo vuoto e cliente ha indirizzo
  useEffect(() => {
    if (data.corriere === RITIRO_IN_SEDE) {
      compilaIndirizzo(INDIRIZZO_RITIRO)
    } else if (indirizzoCliente && !data.indirizzo_via) {
      handleUsaIndirizzoCliente()
    }
  }, [indirizzoCliente])

  useEffect(() => {
    if (!destinatarioSelezionato || isRitiroInSede) return
    const indirizzo = indirizziDestinatario.find(
      (item) => String(item.id) === String(indirizzoDestinatarioId)
    ) || indirizziDestinatario.find((item) => item.predefinito) || indirizziDestinatario[0]
    if (indirizzo && !indirizzoDestinatarioId) {
      setValue('indirizzo_destinatario_id', indirizzo.id)
      compilaDaIndirizzoDestinatario(indirizzo, destinatarioSelezionato)
    }
  }, [destinatarioSelezionato, indirizzoDestinatarioId, isRitiroInSede])

  const corrieriOptions = [
    { value: 'brt', label: 'BRT' },
    { value: 'gls', label: 'GLS' },
    { value: 'ups', label: 'UPS' },
    { value: 'dhl', label: 'DHL' },
    { value: 'fedex', label: 'FedEx' },
    { value: 'sda', label: 'SDA' },
    { value: 'tnt', label: 'TNT' },
    { value: 'poste', label: 'Poste Italiane' },
    { value: 'colauri', label: 'COLAURI' },
    { value: RITIRO_IN_SEDE, label: 'RITIRO IN SEDE' },
    { value: 'altro', label: 'Altro' },
  ]

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Destinatario della spedizione</CardTitle>
          <p className="text-sm text-muted-foreground">
            Una spedizione corrisponde a un solo destinatario e a un solo indirizzo.
            Per un’altra destinazione crea una seconda spedizione sullo stesso ordine.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <input
            type="hidden"
            {...register('destinatario_id', { required: 'Il destinatario è obbligatorio' })}
          />
          <DestinatarioSelector
            value={destinatarioId}
            onChange={handleDestinatarioChange}
            error={errors.destinatario_id?.message}
          />
          {ordine?.cliente && !destinatarioId && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleUsaClienteComeDestinatario}
              disabled={isCreatingFromCliente}
            >
              <UserRound className="mr-2 h-4 w-4" />
              {isCreatingFromCliente ? 'Selezione...' : 'Usa cliente come destinatario'}
            </Button>
          )}

          {destinatarioId && indirizziDestinatario.length > 1 && (
            <div className="space-y-2">
              <Label>Indirizzo del destinatario</Label>
              <Select
                value={indirizzoDestinatarioId ? String(indirizzoDestinatarioId) : undefined}
                onValueChange={handleIndirizzoDestinatarioChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona indirizzo" />
                </SelectTrigger>
                <SelectContent>
                  {indirizziDestinatario.map((indirizzo) => (
                    <SelectItem key={indirizzo.id} value={String(indirizzo.id)}>
                      {indirizzo.etichetta ? `${indirizzo.etichetta} — ` : ''}
                      {indirizzo.via} {indirizzo.civico}, {indirizzo.cap} {indirizzo.citta}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {destinatarioId && destinatarioSelezionato && indirizziDestinatario.length === 0 && (
            <p className="text-sm text-amber-700 rounded-md border border-amber-200 bg-amber-50 p-3">
              Questo destinatario non ha ancora indirizzi salvati. Puoi inserire manualmente
              l’indirizzo qui sotto e aggiungerlo in seguito dalla sua anagrafica.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Corriere e Tracking</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Corriere */}
          <div className="space-y-2">
            <Label htmlFor="corriere">
              Corriere <span className="text-destructive">*</span>
            </Label>
            <input
              type="hidden"
              {...register('corriere', {
                required: 'Il corriere è obbligatorio',
              })}
            />
            <Select
              value={watch('corriere')}
              onValueChange={handleCorriereChange}
            >
              <SelectTrigger className={errors.corriere ? 'border-destructive' : ''}>
                <SelectValue placeholder="Seleziona corriere" />
              </SelectTrigger>
              <SelectContent>
                {corrieriOptions.map((corriere) => (
                  <SelectItem key={corriere.value} value={corriere.value}>
                    {corriere.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.corriere && (
              <p className="text-sm text-destructive">{errors.corriere.message}</p>
            )}
          </div>

          {/* Tracking Number */}
          {!isRitiroInSede && (
            <div className="space-y-2">
              <Label htmlFor="tracking_number">Numero Tracking</Label>
              <Input
                id="tracking_number"
                {...register('tracking_number')}
                placeholder="es: 1234567890"
              />
              <p className="text-sm text-muted-foreground">
                Opzionale - può essere aggiunto successivamente
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{isRitiroInSede ? 'Indirizzo di Ritiro' : 'Indirizzo di Consegna'}</CardTitle>
            {!isRitiroInSede && indirizzoCliente ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleUsaIndirizzoCliente}
              >
                <MapPin className="mr-2 h-4 w-4" />
                Usa indirizzo cliente
              </Button>
            ) : !isRitiroInSede && ordine?.cliente && (
              <span className="text-xs text-muted-foreground italic">
                Nessun indirizzo salvato per questo cliente
              </span>
            )}
          </div>
          {ordine?.cliente && (
            <p className="text-sm text-muted-foreground">
              Cliente: <strong>{ordine.cliente.nome_completo}</strong>
              {indirizzoCliente && (
                <span className="ml-2">
                  ({indirizzoCliente.citta}, {indirizzoCliente.provincia})
                </span>
              )}
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {isRitiroInSede && (
            <p className="text-sm text-muted-foreground">
              Ritiro presso Tavernetta Colauri, Via Comunale Margherita 245, 80145 Napoli (NA).
            </p>
          )}
          {/* Via */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="indirizzo_via">
                Via <span className="text-destructive">*</span>
              </Label>
              <Input
                id="indirizzo_via"
                readOnly={isRitiroInSede}
                {...register('indirizzo_via', {
                  required: 'La via è obbligatoria',
                })}
                placeholder="Via Roma"
              />
              {errors.indirizzo_via && (
                <p className="text-sm text-destructive">{errors.indirizzo_via.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="indirizzo_civico">Civico</Label>
              <Input
                id="indirizzo_civico"
                readOnly={isRitiroInSede}
                {...register('indirizzo_civico')}
                placeholder="10"
              />
            </div>
          </div>

          {/* CAP, Città, Provincia */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="indirizzo_cap">
                CAP <span className="text-destructive">*</span>
              </Label>
              <Input
                id="indirizzo_cap"
                readOnly={isRitiroInSede}
                {...register('indirizzo_cap', {
                  required: 'Il CAP è obbligatorio',
                  pattern: {
                    value: /^\d{5}$/,
                    message: 'Il CAP deve essere di 5 cifre'
                  },
                  maxLength: {
                    value: 5,
                    message: 'Il CAP deve essere di 5 cifre'
                  }
                })}
                placeholder="20100"
                maxLength={5}
              />
              {errors.indirizzo_cap && (
                <p className="text-sm text-destructive">{errors.indirizzo_cap.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="indirizzo_citta">
                Città <span className="text-destructive">*</span>
              </Label>
              <Input
                id="indirizzo_citta"
                readOnly={isRitiroInSede}
                {...register('indirizzo_citta', {
                  required: 'La città è obbligatoria',
                })}
                placeholder="Milano"
              />
              {errors.indirizzo_citta && (
                <p className="text-sm text-destructive">{errors.indirizzo_citta.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="indirizzo_provincia">
                Provincia <span className="text-destructive">*</span>
              </Label>
              <Input
                id="indirizzo_provincia"
                readOnly={isRitiroInSede}
                {...register('indirizzo_provincia', {
                  required: 'La provincia è obbligatoria',
                  pattern: {
                    value: /^[A-Za-z]{2}$/,
                    message: 'La provincia deve essere di 2 lettere (es: MI, RM)'
                  }
                })}
                placeholder="MI"
                maxLength={2}
              />
              {errors.indirizzo_provincia && (
                <p className="text-sm text-destructive">{errors.indirizzo_provincia.message}</p>
              )}
            </div>
          </div>

          {/* Presso */}
          <div className="space-y-2">
            <Label htmlFor="indirizzo_presso">Presso</Label>
            <Input
              id="indirizzo_presso"
              readOnly={isRitiroInSede}
              {...register('indirizzo_presso')}
              placeholder='es: "Portineria", "Ufficio 3"'
            />
          </div>

          {/* Note Indirizzo */}
          <div className="space-y-2">
            <Label htmlFor="indirizzo_note">Note Indirizzo</Label>
            <Textarea
              id="indirizzo_note"
              readOnly={isRitiroInSede}
              {...register('indirizzo_note')}
              placeholder='es: "Suonare al citofono", "Consegna solo mattina"'
              rows={2}
            />
          </div>

          {/* Checkbox salva indirizzo per cliente */}
          {ordine?.cliente && !isRitiroInSede && (
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="salva_indirizzo_cliente"
                checked={watch('salva_indirizzo_cliente') || false}
                onCheckedChange={(checked) => setValue('salva_indirizzo_cliente', checked)}
              />
              <Label
                htmlFor="salva_indirizzo_cliente"
                className="text-sm font-normal cursor-pointer"
              >
                Salva questo indirizzo per il cliente (per riutilizzo futuro)
              </Label>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Note Spedizione</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="note">Note</Label>
            <Textarea
              id="note"
              {...register('note')}
              placeholder="Note aggiuntive sulla spedizione"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack}>
          Annulla
        </Button>
        <Button type="submit">
          Avanti: Colli
        </Button>
      </div>
    </form>
  )
}
